import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Skoda Karoq — a compact SUV at true scale (1 unit = 1 metre): length ~4.39,
// width 1.84, height 1.60 (with rails), wheelbase 2.64, on 17-inch alloys.
// Nose is +Z. Authored for the `cars` showcase batch: spec-sheet proportions,
// a full front/rear fascia (slatted grille, badge, LED headlamps, fog lamps,
// plates, C-shaped tail lamps), tumblehome side glazing, black wheel-arch and
// sill cladding, roof rails, mirrors with indicator strips, five-spoke wheels
// with brake discs, a "paint" recolour slot on every body panel, a
// wheel-spinning `drive` loop and a blinking `hazards` loop.

const PAINT = "#1c4e9d"; // factory Race Blue — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (pillars, grille base, cowl)
const CLAD = "#2b2e33"; // matte plastic cladding (sills, lower bumpers)
const WELL = "#141518"; // wheel wells and arch brows
const GLASS = "#22303e"; // tinted side / rear / pano glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SILVER = "#adb3ba"; // alloys, roof rails, skid plates
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const LED = "#f6fbff";
const AMBER = "#e8a13a";
const RED = "#a91527";
const REDDARK = "#701020";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.35;
const WHEEL_X = 0.84; // half track
const AXLE_F = 1.4;
const AXLE_R = -1.24; // wheelbase 2.64

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One 17-inch alloy wheel: tyre, brake disc, dark rim dish, five silver
 *  spokes, chrome centre cap. Every part shares `name` so the `drive` track
 *  spins them as one node about the axle. `side` is +1 for the right (+x)
 *  wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.235, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.16, 0.16, 0.03, 16, BRAKE, [x - side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.215, 0.215, 0.07, 18, "#202225", [x + side * 0.065, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.05, 0.37, 0.09, "#c4c9cf", [x + side * 0.095, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.055, 0.055, 0.03, 12, CHROME, [x + side * 0.105, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a black plastic brow — an upper half-torus over the tyre. The
 *  [0, pi/2, 0] yaw puts the torus in the YZ plane with its 0..pi arc sweeping
 *  exactly the top half. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.44, 0.44, 0.04, 18, WELL, [side * 0.92, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.41, 0.06, 6, 18, Math.PI),
      color: CLAD,
      position: [side * 0.945, WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
    },
  ];
}

// The six hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.86, 0.9, 2.15], size: [0.05, 0.05, 0.05] },
  { name: "indicator-fr", pos: [0.86, 0.9, 2.15], size: [0.05, 0.05, 0.05] },
  { name: "indicator-rl", pos: [-0.665, 1.06, -2.16], size: [0.1, 0.05, 0.03] },
  { name: "indicator-rr", pos: [0.665, 1.06, -2.16], size: [0.1, 0.05, 0.03] },
  { name: "indicator-ml", pos: [-1.025, 1.105, 0.42], size: [0.018, 0.018, 0.09] },
  { name: "indicator-mr", pos: [1.025, 1.105, 0.42], size: [0.018, 0.018, 0.09] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.56, 0.16, 3.4, TRIM, [0, 0.36, 0.08])); // underbody pan

  // -- Body masses (paint) ----------------------------------------------------
  P.push(paint(1.82, 0.49, 4.17, [0, 0.695, -0.065])); // main tub, sill to shoulder
  P.push(paint(1.82, 0.07, 3.18, [0, 0.975, -0.54])); // belt-line band, cowl to tail
  P.push(paint(1.72, 0.09, 1.11, [0, 0.945, 1.585], [0.054, 0, 0])); // bonnet
  for (const s of S) P.push(paint(0.07, 0.08, 1.11, [s * 0.875, 0.965, 1.585], [0.054, 0, 0])); // fender tops
  P.push(paint(1.84, 0.38, 0.15, [0, 0.64, 2.095])); // nose block
  P.push(paint(1.84, 0.37, 0.12, [0, 0.635, -2.11])); // rear bumper
  P.push(paint(1.82, 0.14, 0.36, [0, 1.09, -1.98])); // rear shoulder / tailgate top

  // -- Black cladding: sills and lower bumpers --------------------------------
  for (const s of S) P.push(box(0.05, 0.16, 1.78, CLAD, [s * 0.915, 0.42, 0.08]));
  P.push(box(1.78, 0.28, 0.28, CLAD, [0, 0.42, 2.03])); // front lower bumper, back to the arch
  P.push(box(1.78, 0.28, 0.44, CLAD, [0, 0.42, -1.95])); // rear lower bumper, back to the arch
  P.push(box(0.78, 0.09, 0.06, SILVER, [0, 0.345, 2.16])); // front skid plate
  P.push(box(0.9, 0.09, 0.05, SILVER, [0, 0.345, -2.16])); // rear skid plate

  // -- Greenhouse -------------------------------------------------------------
  P.push(box(1.44, 0.03, 0.12, TRIM, [0, 1.005, 1.0])); // cowl vent
  P.push(box(1.5, 0.72, 0.045, SCREEN, [0, 1.24, 0.775], [-0.874, 0, 0])); // windscreen
  for (const s of S) P.push(paint(0.12, 0.76, 0.09, [s * 0.8, 1.24, 0.775], [-0.874, 0, 0])); // A-pillars
  for (const s of S) {
    P.push(box(0.035, 0.46, 1.85, GLASS, [s * 0.825, 1.24, -0.48], [0, 0, s * 0.2])); // side glass band
    P.push(box(0.045, 0.46, 0.09, TRIM, [s * 0.83, 1.24, -0.3], [0, 0, s * 0.2])); // B-pillar
    P.push(box(0.045, 0.46, 0.06, TRIM, [s * 0.83, 1.24, -0.95], [0, 0, s * 0.2])); // rear-door divider
    P.push(paint(0.06, 0.48, 0.44, [s * 0.825, 1.24, -1.63], [0, 0, s * 0.2])); // C-pillar
    P.push(box(0.02, 0.025, 1.9, CHROME, [s * 0.875, 1.01, -0.48])); // belt-line strip
  }
  P.push(paint(1.6, 0.07, 2.35, [0, 1.515, -0.675])); // roof, front edge on the screen top
  P.push(box(1.18, 0.025, 1.0, GLASS, [0, 1.542, -0.15])); // panoramic roof
  for (const s of S) P.push(box(0.045, 0.045, 1.6, SILVER, [s * 0.7, 1.575, -0.68])); // roof rails
  P.push(box(0.05, 0.07, 0.18, TRIM, [0, 1.583, -1.55])); // shark-fin antenna

  // -- Front fascia -----------------------------------------------------------
  P.push(box(0.96, 0.28, 0.05, TRIM, [0, 0.81, 2.13])); // grille base
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.024, 0.24, 0.02, "#42464c", [i * 0.17, 0.81, 2.155])); // vertical slats
  }
  P.push(cyl(0.055, 0.055, 0.02, 16, CHROME, [0, 0.88, 2.17], [Math.PI / 2, 0, 0])); // badge
  for (const s of S) {
    P.push(box(0.4, 0.15, 0.02, TRIM, [s * 0.69, 0.895, 2.13])); // headlamp surround
    P.push(box(0.36, 0.12, 0.05, LAMP, [s * 0.69, 0.895, 2.15])); // headlamp
    P.push(box(0.3, 0.03, 0.012, LED, [s * 0.69, 0.93, 2.18])); // DRL bar
    P.push(box(0.3, 0.05, 0.012, "#8fa5b2", [s * 0.69, 0.875, 2.18])); // projector band
    P.push(box(0.08, 0.08, 0.03, LAMP, [s * 0.7, 0.44, 2.168])); // fog lamp
  }
  P.push(box(1.2, 0.13, 0.04, TRIM, [0, 0.44, 2.16])); // lower intake
  P.push(box(0.6, 0.16, 0.015, TRIM, [0, 0.6, 2.176])); // plate recess
  P.push(box(0.52, 0.11, 0.02, PLATE, [0, 0.6, 2.186])); // front plate

  // -- Rear fascia ------------------------------------------------------------
  P.push(box(1.4, 0.35, 0.04, GLASS, [0, 1.315, -1.94], [0.476, 0, 0])); // rear screen
  for (const s of S) P.push(paint(0.18, 0.35, 0.06, [s * 0.77, 1.315, -1.935], [0.476, 0, 0])); // D-pillars
  P.push(paint(1.5, 0.06, 0.26, [0, 1.52, -1.97])); // roof spoiler, flush with the roof top
  P.push(box(0.55, 0.02, 0.02, RED, [0, 1.475, -2.09])); // high brake light
  for (const s of S) {
    // C-shaped tail lamp at the window-base corner: outer drop + two arms
    // opening toward the centre, a dark inner fill behind them.
    P.push(box(0.11, 0.2, 0.1, RED, [s * 0.82, 1.06, -2.13])); // outer vertical
    P.push(box(0.24, 0.06, 0.1, RED, [s * 0.665, 1.13, -2.13])); // top arm
    P.push(box(0.24, 0.06, 0.1, RED, [s * 0.665, 0.99, -2.13])); // bottom arm
    P.push(box(0.18, 0.1, 0.04, REDDARK, [s * 0.68, 1.06, -2.11])); // inner fill
    P.push(box(0.024, 0.12, 0.1, RED, [s * 0.9, 1.06, -2.1])); // side wrap
    P.push(box(0.05, 0.03, 0.02, RED, [s * 0.75, 0.47, -2.18])); // reflector
  }
  P.push(box(0.5, 0.022, 0.012, CHROME, [0, 0.88, -2.16])); // tailgate chrome strip
  P.push(box(0.6, 0.16, 0.015, TRIM, [0, 0.72, -2.176])); // plate recess
  P.push(box(0.52, 0.12, 0.02, PLATE, [0, 0.72, -2.186])); // rear plate

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.09, 0.025, 0.04, TRIM, [s * 0.92, 1.085, 0.42])); // mirror arm
    P.push(paint(0.1, 0.09, 0.15, [s * 0.98, 1.105, 0.4])); // mirror cap
    P.push(box(0.09, 0.07, 0.012, CHROME, [s * 0.98, 1.105, 0.318])); // mirror glass
    P.push(box(0.02, 0.025, 0.13, CHROME, [s * 0.925, 0.9, -0.05])); // front door handle
    P.push(box(0.02, 0.025, 0.13, CHROME, [s * 0.925, 0.9, -0.9])); // rear door handle
    P.push(box(0.36, 0.012, 0.03, TRIM, [s * 0.33, 1.03, 1.045], [-0.874, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const skodaKaroq = defineModel({
  id: "skoda-karoq",
  name: "Skoda Karoq",
  batchId: "cars",
  tags: ["car", "skoda", "karoq", "suv", "vehicle", "modern"],
  summary: "A Skoda Karoq compact SUV at true scale, in factory Race Blue.",
  description:
    "A Skoda Karoq compact SUV at real 1 unit = 1 metre scale (about 4.39 " +
    "long, 1.84 wide, 1.60 tall, 2.64 wheelbase, 17-inch alloys). Full " +
    "exterior detail: slatted grille with badge, LED headlamps with DRL " +
    "bars, fog lamps, both number plates, C-shaped tail lamps, roof spoiler " +
    "with high brake light, panoramic roof, roof rails, shark-fin antenna, " +
    "mirrors with indicator strips, black arch and sill cladding, five-spoke " +
    "alloys over brake discs. Body panels carry the 'paint' recolour slot " +
    "(factory Race Blue); `drive` spins the wheels and `hazards` blinks all " +
    "six indicators.",
  author: "Fable 5",
  footprint: 2.35,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
